import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/Button';
import { ScrollAnimation } from '../../components/common/ScrollAnimation';
import { Camera, Video, Heart, Users, Sparkles, Award, Clock, Play, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { YouTubeFacade } from '../../components/common/YouTubeFacade';

// Replace with the studio's own YouTube video ID.
const SHOWREEL_VIDEO_ID = 'AJQcnMbkXGA';

export const HomePage: React.FC = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section
        className="relative text-white py-32 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #701a75 0%, #4c1d95 35%, #1e1b4b 65%, #0c0a47 100%)' }}
      >
        {/* Animated blobs */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute top-20 left-10 w-80 h-80 rounded-full blur-3xl opacity-30"
            style={{ background: 'radial-gradient(circle, #d946ef, transparent)' }}
            animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute top-40 right-10 w-80 h-80 rounded-full blur-3xl opacity-25"
            style={{ background: 'radial-gradient(circle, #f43f5e, transparent)' }}
            animate={{ x: [0, -100, 0], y: [0, -50, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute bottom-20 left-1/2 w-96 h-96 rounded-full blur-3xl opacity-20"
            style={{ background: 'radial-gradient(circle, #8b5cf6, transparent)' }}
            animate={{ x: [0, 60, 0], y: [0, -40, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
          />
        </div>

        {/* Floating icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[
            { Icon: Camera, top: '15%', left: '20%', size: 'w-14 h-14', color: 'text-primary-300/25', dur: 8 },
            { Icon: Video, top: '30%', right: '20%', size: 'w-10 h-10', color: 'text-rose-300/20', dur: 10 },
            { Icon: Heart, bottom: '30%', left: '30%', size: 'w-12 h-12', color: 'text-amber-300/20', dur: 12 },
            { Icon: Sparkles, top: '55%', right: '12%', size: 'w-9 h-9', color: 'text-cyan-300/15', dur: 9 },
            { Icon: Star, bottom: '18%', right: '30%', size: 'w-8 h-8', color: 'text-emerald-300/18', dur: 7 },
            { Icon: Award, top: '25%', left: '12%', size: 'w-10 h-10', color: 'text-violet-300/15', dur: 11 },
          ].map(({ Icon, size, color, dur, ...pos }, i) => (
            <motion.div
              key={i}
              className={`absolute ${size} ${color}`}
              style={pos as any}
              animate={{ rotate: i % 2 === 0 ? [0, 360] : [360, 0], x: [0, 40, 0], y: [0, 30, 0] }}
              transition={{ rotate: { duration: dur, repeat: Infinity, ease: 'linear' }, x: { duration: dur - 2, repeat: Infinity, ease: 'easeInOut' }, y: { duration: dur - 4, repeat: Infinity, ease: 'easeInOut' } }}
            >
              <Icon className="w-full h-full" />
            </motion.div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={false}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="rounded-3xl p-12 md:p-16 border border-white/20 shadow-2xl"
              style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(16px)' }}
            >
              <motion.div
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold mb-6 border border-primary-400/40"
                style={{ background: 'rgba(217,70,239,0.2)', color: '#f0abfc' }}
              >
                <Sparkles className="h-4 w-4" /> Professional Photography & Videography
              </motion.div>
              <motion.h1
                initial={false}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-4xl md:text-6xl font-bold mb-4 drop-shadow-lg"
              >
                Capturing Your{' '}
                <span
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(90deg, #f0abfc, #fda4af, #fcd34d)' }}
                >
                  Precious Moments
                </span>
              </motion.h1>
              <motion.p
                initial={false}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="text-xl md:text-2xl mb-8 text-purple-200 drop-shadow-md"
              >
                Professional Photography &amp; Videography Services in Pudukkottai
              </motion.p>
              <motion.div
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Link to="/contact">
                  <Button variant="secondary" size="lg" className="transform hover:scale-105 transition-transform shadow-xl shadow-rose-500/30">
                    Book Your Event
                  </Button>
                </Link>
                <Link to="/gallery">
                  <Button variant="outline" size="lg" className="border-white/40 text-white hover:bg-white/20 hover:text-white transform hover:scale-105 transition-transform shadow-lg backdrop-blur-sm">
                    View Gallery
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #fdf4ff 0%, #fae8ff 50%, #fdf4ff 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 rounded-full blur-3xl opacity-20" style={{ background: '#d946ef' }} />
          <div className="absolute bottom-10 right-10 w-72 h-72 rounded-full blur-3xl opacity-15" style={{ background: '#f43f5e' }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 gradient-text">Our Services</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto text-lg">
              Comprehensive photography and videography services to capture your most precious moments
            </p>
          </ScrollAnimation>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Heart, label: 'Weddings', desc: 'Capture your special day with our professional wedding photography', gradient: 'from-rose-500 to-pink-600', shadow: 'shadow-rose-400/30', bg: 'from-rose-50 to-pink-50', border: 'border-rose-200' },
              { icon: Users, label: 'Baby Photography', desc: 'Preserve precious moments of your little ones', gradient: 'from-amber-400 to-orange-500', shadow: 'shadow-amber-400/30', bg: 'from-amber-50 to-orange-50', border: 'border-amber-200' },
              { icon: Camera, label: 'Pre-Wedding', desc: 'Beautiful pre-wedding shoots to celebrate your love', gradient: 'from-violet-500 to-purple-600', shadow: 'shadow-violet-400/30', bg: 'from-violet-50 to-purple-50', border: 'border-violet-200' },
              { icon: Video, label: 'Candid', desc: 'Cinematic candid wedding films and event coverage', gradient: 'from-cyan-500 to-teal-600', shadow: 'shadow-cyan-400/30', bg: 'from-cyan-50 to-teal-50', border: 'border-cyan-200' },
            ].map(({ icon: Icon, label, desc, gradient, shadow, bg, border }, index) => (
              <ScrollAnimation key={label} direction="up" delay={index * 0.1}>
                <motion.div
                  whileHover={{ y: -15, scale: 1.05, rotate: index % 2 === 0 ? 2 : -2 }}
                  className={`text-center bg-gradient-to-br ${bg} backdrop-blur-sm p-8 rounded-2xl shadow-lg ${shadow} hover:shadow-2xl transition-all border ${border}`}
                >
                  <div className={`bg-gradient-to-br ${gradient} rounded-full p-5 w-20 h-20 mx-auto mb-6 flex items-center justify-center shadow-lg ${shadow}`}>
                    <Icon className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">{label}</h3>
                  <p className="text-gray-600">{desc}</p>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* Colorful divider */}
      <div className="h-2 w-full bg-gradient-to-r from-primary-500 via-rose-500 via-amber-500 via-emerald-500 to-cyan-500" />

      {/* Video Section */}
      <section className="py-20 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0c0a47 0%, #1e1b4b 40%, #4c1d95 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: '#d946ef' }} />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-15" style={{ background: '#f43f5e' }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollAnimation direction="fade">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Transforming{' '}
                <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, #f0abfc, #fda4af)' }}>
                  Genuine Happiness
                </span>{' '}
                into Eternal Imagery!
              </h2>
              <p className="text-xl text-purple-200 max-w-3xl mx-auto">
                What is more fantastic than a perfect dreamy wedding? It is an honor for us to be there to witness
                your special day with our candid wedding films that you never dreamt of.
              </p>
            </div>
          </ScrollAnimation>
          <ScrollAnimation direction="up" delay={0.2}>
            <div
              className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/20"
              style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(8px)', padding: '6px' }}
            >
              {/* Gradient border glow */}
              <div className="absolute inset-0 rounded-3xl opacity-60 blur-sm pointer-events-none" style={{ background: 'linear-gradient(135deg, #d946ef, #f43f5e, #f59e0b, #10b981)' }} />
              <div className="relative rounded-2xl overflow-hidden" style={{ background: '#000' }}>
                {/* Video label bar */}
                <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-3" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
                    <span className="text-white text-xs font-semibold tracking-wider uppercase">Live Reel</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-white/70 text-xs">
                    <Play className="h-3 w-3" /> Wedding Highlights
                  </div>
                </div>

                {/* YouTube Embed */}
                <div className="aspect-video">
                  <YouTubeFacade
                    videoId={SHOWREEL_VIDEO_ID}
                    title="Wedding Highlights — Aanand Video Coverage"
                    poster="https://images.unsplash.com/photo-1583939003579-730e3918a45a?auto=format&fit=crop&w=800&q=60"
                  />
                </div>

                {/* Bottom bar */}
                <div className="absolute bottom-0 left-0 right-0 z-10 px-4 py-3 flex items-center justify-between" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75), transparent)' }}>
                  <div>
                    <p className="text-white font-semibold text-sm">Candid Wedding Films</p>
                    <p className="text-purple-200 text-xs">Aanand Video Coverage · Pudukkottai</p>
                  </div>
                  <div className="flex gap-1">
                    {['🎬', '💍', '✨'].map((e, i) => (
                      <motion.span
                        key={i}
                        className="text-lg"
                        animate={{ y: [0, -4, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.3 }}
                      >
                        {e}
                      </motion.span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </ScrollAnimation>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #fdf4ff 0%, #fff1f2 50%, #fffbeb 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 gradient-text">Why Choose Us?</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              We take our craft seriously and are committed to delivering exceptional results
            </p>
          </ScrollAnimation>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Award, title: 'Award Winning', desc: 'Recognized for excellence in photography and videography', gradient: 'from-amber-400 to-orange-500', shadow: 'shadow-amber-400/30', bg: 'from-amber-50 to-orange-50 border-amber-200' },
              { icon: Sparkles, title: 'Creative Excellence', desc: 'Unique and artistic approach to every shoot', gradient: 'from-violet-500 to-purple-600', shadow: 'shadow-violet-400/30', bg: 'from-violet-50 to-purple-50 border-violet-200' },
              { icon: Clock, title: 'On-Time Delivery', desc: 'We deliver your memories on time, every time', gradient: 'from-emerald-500 to-teal-600', shadow: 'shadow-emerald-400/30', bg: 'from-emerald-50 to-teal-50 border-emerald-200' },
            ].map(({ icon: Icon, title, desc, gradient, shadow, bg }, index) => (
              <ScrollAnimation key={title} direction="up" delay={index * 0.1}>
                <motion.div
                  whileHover={{ y: -10 }}
                  className={`text-center p-8 rounded-2xl bg-gradient-to-br ${bg} border shadow-lg hover:shadow-xl transition-all`}
                >
                  <div className={`bg-gradient-to-br ${gradient} rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center shadow-lg ${shadow}`}>
                    <Icon className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-800">{title}</h3>
                  <p className="text-gray-600">{desc}</p>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #701a75 0%, #9d174d 50%, #7c2d12 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-20 w-96 h-96 rounded-full blur-3xl opacity-15" style={{ background: '#fcd34d' }} />
          <div className="absolute bottom-0 left-20 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#f0abfc' }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollAnimation direction="fade">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { value: '1000+', label: 'Weddings', gradient: 'from-primary-400 to-violet-400' },
                { value: '30+', label: 'Years Experience', gradient: 'from-rose-400 to-pink-400' },
                { value: '100+', label: 'Locations', gradient: 'from-amber-400 to-orange-400' },
                { value: '5000+', label: 'Happy Clients', gradient: 'from-emerald-400 to-cyan-400' },
              ].map(({ value, label, gradient }, i) => (
                <motion.div
                  key={label}
                  initial={{ scale: 0, rotate: -180 }}
                  whileInView={{ scale: 1, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, type: 'spring', stiffness: 200 }}
                  className="rounded-2xl p-6 border border-white/20 shadow-xl"
                  style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)' }}
                >
                  <div className={`text-5xl md:text-7xl font-bold mb-2 bg-gradient-to-br ${gradient} bg-clip-text text-transparent`}>
                    {value}
                  </div>
                  <div className="text-pink-100 text-lg font-medium">{label}</div>
                </motion.div>
              ))}
            </div>
          </ScrollAnimation>
        </div>
      </section>

      {/* Featured Gallery Section */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #fdf4ff 0%, #f5d0fe 50%, #fdf4ff 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 gradient-text">Our Recent Work</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto text-lg">
              Explore some of our beautiful captures from recent events
            </p>
          </ScrollAnimation>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { src: 'https://images.unsplash.com/photo-1606216794074-735e91aa2c92?auto=format&fit=crop&w=700&q=80', label: 'Wedding Day', cat: 'Weddings' },
              { src: 'https://images.unsplash.com/photo-1544126592-807ade215a0b?auto=format&fit=crop&w=700&q=80', label: 'Newborn Portrait', cat: 'Baby Photography' },
              { src: 'https://images.unsplash.com/photo-1621621667797-e06afc217fb0?auto=format&fit=crop&w=700&q=80', label: 'Pre-Wedding Shoot', cat: 'Pre-Wedding' },
              { src: 'https://images.unsplash.com/photo-1617627143750-d86bc21e42bb?auto=format&fit=crop&w=700&q=80', label: 'Bridal Portrait', cat: 'Weddings' },
              { src: 'https://images.unsplash.com/photo-1604017011826-d3b4c23f8914?auto=format&fit=crop&w=700&q=80', label: 'Outdoor Session', cat: 'Outdoor' },
              { src: 'https://images.unsplash.com/photo-1494774157365-9e04c6720e47?auto=format&fit=crop&w=700&q=80', label: 'Romantic Couple', cat: 'Pre-Wedding' },
            ].map((item, index) => (
              <ScrollAnimation key={index} direction="up" delay={index * 0.1}>
                <motion.div
                  whileHover={{ scale: 1.05, y: -10 }}
                  className="relative overflow-hidden rounded-2xl shadow-xl group cursor-pointer border border-white/60"
                >
                  <div className="aspect-[4/3] relative overflow-hidden bg-gradient-to-br from-primary-200 to-violet-300">
                    <img
                      src={item.src}
                      alt={item.label}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <p className="text-xs text-white/70 mb-1 uppercase tracking-wider">{item.cat}</p>
                        <h3 className="font-semibold text-lg">{item.label}</h3>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
          <ScrollAnimation direction="fade" delay={0.5}>
            <div className="text-center mt-12">
              <Link to="/gallery">
                <Button variant="primary" size="lg" className="transform hover:scale-105 transition-transform shadow-lg shadow-primary-500/30">
                  View Full Gallery
                </Button>
              </Link>
            </div>
          </ScrollAnimation>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #fae8ff 50%, #fff1f2 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 gradient-text">What Our Clients Say</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto text-lg">
              Don't just take our word for it — hear from our happy clients
            </p>
          </ScrollAnimation>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: 'Sneha Hariharan', text: 'Thanks to the entire crew who came for outdoor shoot and the wedding shoot. They gave their best. Amazing work!', rating: 5, accent: 'from-rose-500 to-pink-600', bg: 'from-rose-50 to-pink-50 border-rose-200' },
              { name: 'Pooja Elangovan', text: 'The pictures turned out beautiful! We got the complimentary frame & magazine and it is more than we imagined.', rating: 5, accent: 'from-violet-500 to-purple-600', bg: 'from-violet-50 to-purple-50 border-violet-200' },
              { name: 'Mansoor Khan', text: 'Thank you so much for such a beautiful collection of photos. There were so many stunning photos to choose from.', rating: 5, accent: 'from-amber-500 to-orange-600', bg: 'from-amber-50 to-orange-50 border-amber-200' },
            ].map((t, index) => (
              <ScrollAnimation key={index} direction="up" delay={index * 0.1}>
                <motion.div
                  whileHover={{ y: -10, scale: 1.02 }}
                  className={`bg-gradient-to-br ${t.bg} p-8 rounded-2xl shadow-lg border hover:shadow-xl transition-all`}
                >
                  <div className="flex items-center mb-4">
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={i} className="text-amber-400 fill-amber-400 h-5 w-5" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic text-lg leading-relaxed">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${t.accent} flex items-center justify-center text-white font-bold text-lg shadow-md`}>
                      {t.name[0]}
                    </div>
                    <p className="font-semibold text-gray-900 text-lg">{t.name}</p>
                  </div>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #ecfeff 0%, #fdf4ff 50%, #fff7ed 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 gradient-text">How We Work</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto text-lg">
              Our simple process ensures your special day is captured perfectly
            </p>
          </ScrollAnimation>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'We discuss your vision and requirements', color: 'from-cyan-500 to-blue-600', shadow: 'shadow-cyan-400/30', bg: 'from-cyan-50 to-blue-50 border-cyan-200' },
              { step: '02', title: 'Planning', desc: 'We plan every detail of your shoot to deliver stunning results.', color: 'from-violet-500 to-purple-600', shadow: 'shadow-violet-400/30', bg: 'from-violet-50 to-purple-50 border-violet-200' },
              { step: '03', title: 'Capture', desc: 'We capture your special moments beautifully', color: 'from-rose-500 to-pink-600', shadow: 'shadow-rose-400/30', bg: 'from-rose-50 to-pink-50 border-rose-200' },
              { step: '04', title: 'Delivery', desc: 'We deliver your memories in stunning quality', color: 'from-emerald-500 to-teal-600', shadow: 'shadow-emerald-400/30', bg: 'from-emerald-50 to-teal-50 border-emerald-200' },
            ].map((item, index) => (
              <ScrollAnimation key={index} direction="up" delay={index * 0.1}>
                <motion.div
                  whileHover={{ y: -10 }}
                  className={`text-center bg-gradient-to-br ${item.bg} border p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all`}
                >
                  <div className={`text-5xl font-black mb-4 bg-gradient-to-br ${item.color} bg-clip-text text-transparent`}>
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-800">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #881337 0%, #9d174d 30%, #701a75 60%, #4c1d95 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-20 -mr-48 -mt-48" style={{ background: '#fcd34d' }} />
          <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-15 -ml-48 -mb-48" style={{ background: '#a5f3fc' }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <ScrollAnimation direction="fade">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Ready to Capture Your{' '}
                <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, #fcd34d, #fda4af)' }}>
                  Special Moments?
                </span>
              </h2>
              <p className="text-xl text-pink-100 mb-8 max-w-2xl mx-auto">
                Get in touch with us and let us make your special day an event of your lifetime
              </p>
              <Link to="/contact">
                <Button variant="secondary" size="lg" className="transform hover:scale-110 transition-transform shadow-2xl shadow-amber-500/30">
                  Contact Us Now
                </Button>
              </Link>
            </motion.div>
          </ScrollAnimation>
        </div>
      </section>
    </Layout>
  );
};
